import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './core/core.scss'
import './core/index.scss'

import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

// Real pages (paths are now relative to src/)
import { NotFoundPage } from './routes/404'
import DashboardPage from './routes/dashboard'
import LoginPage from './routes/login'
import ModelsPage from './routes/models'
import ConfirmationEmailPage from './routes/resend-confirmation-email'
import ResetPasswordPage from './routes/reset-password'
import { SignUpPage } from './routes/sign-up'

// Route guards — rewritten for react-router v6 (see PublicRoute.tsx / ProtectedRoute.tsx)
import ProtectedRoute from './routes/protected-route'
import PublicRoute from './routes/public-route'

// Context providers
import { AccountProvider } from './context/account'
import { ExploreProvider } from './context/explore'
import { LocationsProvider } from './context/locations'
import { MapDataProvider } from './context/map-data'
import { OrganisationProvider } from './context/organisation'
// add to imports
import { getStoredConsent, initAnalytics } from './analytics/analytics'
import { CookieConsent } from './analytics/cookie-consent'
import { usePageTracking } from './analytics/use-page-tracking'

// after Sentry.init(...): returning users who already accepted get GA without re-prompting
if (getStoredConsent() === 'granted') {
  initAnalytics()
}

// a tiny component so the hook runs *inside* BrowserRouter (useLocation needs the router)
const RouteTracker = () => {
  usePageTracking()
  return null
}
const MODE = import.meta.env.MODE

// Single Sentry init (the one that was in [...].tsx has been dropped)
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  sendDefaultPii: true,
  environment: MODE,
  enabled: ['production', 'staging'].includes(MODE),
})

const App = () => (
  // Providers inside BrowserRouter so any of them may use router hooks if needed.
  (<BrowserRouter>
    <AccountProvider>
      <OrganisationProvider>
        <MapDataProvider>
          <LocationsProvider>
            <ExploreProvider>
              <RouteTracker />
              <Routes>
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/sign-up" element={<PublicRoute><SignUpPage /></PublicRoute>} />
                <Route
                  path="/reset-password"
                  element={<PublicRoute><ResetPasswordPage /></PublicRoute>}
                />
                <Route
                  path="/confirm-email"
                  element={<PublicRoute><ConfirmationEmailPage /></PublicRoute>}
                />
                {/* Public but does NOT redirect logged-in users away */}
                <Route
                  path="/models/*"
                  element={
                    <PublicRoute redirectWhenLoggedIn={false}>
                      <ModelsPage />
                    </PublicRoute>
                  }
                />
                {/* Default application page is the dashboard */}
                <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route
                  path="/dashboard"
                  element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <CookieConsent />
            </ExploreProvider>
          </LocationsProvider>
        </MapDataProvider>
      </OrganisationProvider>
    </AccountProvider>
  </BrowserRouter>)
)

const container = document.getElementById('root')
if (!container) {
  throw new Error('Failed to find the root element')
}
createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
